import {
  Box, Grid,
} from '@mui/material';
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import HeaderMenuSettingsModal from '../shared/HeaderMenuSettingsModal';
import BlockHeaderData from './BlockHeaderData';
import styles from './BlockHeader.module.css';
import HeaderMenu from '../shared/HeaderMenu';
import BlockProps from '../../../../models/BlockProps';
import BlockData from '../../../../models/BlockData';
import Utils from '../../../../utils/Utils';
import CloseButton from '../../../Buttons/Close';
import EditableText from '../../../Form/EditableText';
import LinkData from '../../../../models/LinkData';
import HamburgerMenuButton from '../../../Buttons/HamburgerMenuButton';
import BlockComponentImage from '../../../../models/BlockComponentValues/BlockComponentImage';
import useGetBackground from '../../../../hooks/UseGetBackground';
import BackGroundImageModal from '../../../Modals/ImageModal/BackGroundImageModal';
import useParallaxScript from '../../../../hooks/useParallaxScript';
import config from '../../../../config';
import { BlockSeparator } from '../../../../models/BlockSettings';
import AutoCloseMenus from '../shared/AutoCloseMenus';

export interface BlockHeaderProps extends BlockProps {
  blockData: BlockData[],
  backgroundColor?: string,
}

function BlockHeader({
  data,
  isEditing,
  isTemplate,
  editHandler,
  blockData,
  showImageModal,
  onCloseImageModal,
  backgroundColor,
}: BlockHeaderProps) {
  const [props, setProps] = useState<BlockHeaderData>(data.data as BlockHeaderData);

  useEffect(() => {
    setProps(data.data as BlockHeaderData);
  }, [data.data]);

  const hamburgerCheckbox = useRef(null);
  const [open, setOpen] = useState(false);
  const [backgroundColorState, setBackgroundColorState] = useState<string | undefined>(backgroundColor);
  const [showMenu, setShowMenu] = useState(false);

  // useResponsive({
  //   items: [
  //     {
  //       wrapperId: 'wrapperId',
  //       targetId: 'hamburger',
  //       queries: [
  //         { size: 0, className: styles.displayButton },
  //         { size: 500, className: styles.hiddenButton },
  //       ],
  //     },
  //     {
  //       wrapperId: 'wrapperId',
  //       targetId: 'hamburgerMenu',
  //       queries: [
  //         { size: 0, className: styles.displayMenu },
  //         { size: 500, className: styles.hiddenMenu },
  //       ],
  //     },
  //     {
  //       wrapperId: 'wrapperId',
  //       targetId: 'menu',
  //       queries: [
  //         { size: 0, className: styles.hidden },
  //         { size: 500, className: styles.flex },
  //       ],
  //     },
  //   ],
  // });

  const handleOpen = useCallback(() => {
    if (isEditing && !isTemplate) {
      setOpen(true);
    }
  }, [isEditing, isTemplate]);
  const handleClose = () => setOpen(false);

  const handleUpdate = (text: string) => {
    props.title.value = text;
    editHandler(data.id, props);
  };

  const handleMenuDelete = useCallback(() => {
    props.menus = undefined;
    editHandler(data.id, props);
  }, [data.id, editHandler, props]);

  const hamburgerButton = useMemo(() => (
    <Grid
      id="hamburger"
      className="hamburgerButton previewResponsiveShow"
      item
      xs="auto"
      justifyContent="end"
    >
      {isEditing || isTemplate ? (
        <HamburgerMenuButton />
      ) : (
        <label htmlFor={`hamburger-${data.id}`}>
          <HamburgerMenuButton />
        </label>
      )}
    </Grid>
  ), [isEditing, isTemplate]);

  const hamburgerMenu = useMemo(() => {
    if (props.menus && props.menus.length > 0) {
      return (
        <Grid
          id="hamburgerMenu"
          className="hamburgerMenu previewResponsiveShow"
          container
          justifyContent="center"
          alignItems="center"
          direction="column"
          sx={{
            p: 1.5,
            background: props.url?.solidColor ?? backgroundColor,
          }}
        >
          <Grid item xs={12} sx={{ width: '100%' }}>
            <HeaderMenu
              menus={props.menus}
              headerSettings={props.settings}
              isEditing={isEditing}
              direction="column"
              handleMenuDelete={handleMenuDelete}
              handleOpen={handleOpen}
            />
          </Grid>
        </Grid>
      );
    }
    return null;
  }, [props.menus, props.settings, isEditing, handleMenuDelete, handleOpen]);

  const menuItems = useMemo(() => {
    if (props.menus && props.menus?.length > 0) {
      return (
        <Grid
          container
          id="menu"
          className="headerMenu"
          justifyContent="center"
          alignItems="center"
        >
          <HeaderMenu
            showSeparators
            menus={props.menus}
            headerSettings={props.settings}
            isEditing={isEditing}
            handleMenuDelete={handleMenuDelete}
            handleOpen={handleOpen}
          />
        </Grid>
      );
    }
    return null;
  }, [props.menus, props.settings, isEditing, handleMenuDelete, handleOpen]);

  // effects
  useEffect(() => {
    if (!props.settings?.separator) {
      setProps({ ...props, settings: { ...props.settings, separator: BlockSeparator.NONE } });
    }
  }, [props]);

  useEffect(() => {
    if (isEditing && hamburgerCheckbox.current?.checked) {
      hamburgerCheckbox.current.checked = false;
    }
  }, [isEditing]);

  useEffect(() => {
    setBackgroundColorState(props.url?.solidColor ?? backgroundColor);
  }, [props.url?.solidColor, backgroundColor]);

  useEffect(() => {
    AutoCloseMenus();
  }, [data]);

  useParallaxScript(props.url?.settings?.parallaxBool, 'wrapperId', config.backgroundImage.parallaxEffect);

  return (
    <>
      <Grid
        container
        spacing={0}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        className={`${styles.header} ${!props.isFree ? 'blockHeaderType' : ''}`}
        width="100%"
        id="wrapperId"
        sx={{
          overflow: 'hidden',
          ...useGetBackground(
            backgroundColorState,
            props.url?.value,
            props.url?.settings?.scale,
            props.url?.settings?.scaleBool,
          ),
          containerType: 'inline-size',
          backgroundPosition: 'center 0px',
        }}
      >
        {(isEditing && !isTemplate) && (
          <>
            <BackGroundImageModal
              isShowModal={showImageModal}
              onCloseModal={onCloseImageModal}
              backgroundImage={props.url}
              defaultBackgroundColor={backgroundColor}
              handleOnConfirm={(url: BlockComponentImage) => {
                const tempBg = { ...props, url: { ...url } };
                setProps(tempBg);
                editHandler(data.id, tempBg);
              }}
            />
          </>
        )}
        <Grid container justifyContent="space-between" alignItems="center" wrap="nowrap" sx={{ p: 1.5 }}>
          {props.title ? (
            <Grid item sx={{ paddingRight: 2 }}>
              <Box sx={[Utils.hoverBorder(isEditing), { width: '100%' }]}>
                <CloseButton
                  show={isEditing}
                  updateHandler={() => {
                    props.title = undefined;
                    editHandler(data.id, props);
                  }}
                />

                <EditableText
                  text={props.title.value}
                  textColor={props.title.settings?.textColor}
                  isEditable={isEditing && !isTemplate}
                  updateHandler={handleUpdate}
                />
              </Box>
            </Grid>
          ) : (
            <Grid item />
          )}

          <Grid item alignItems="center" justifyContent="end">
            {hamburgerButton}
            {menuItems}
          </Grid>
        </Grid>

        <input
          style={{ display: 'none' }}
          type="checkbox"
          id={`hamburger-${data.id}`}
          data-target={`hamburger-menu-${data.id}`}
          className="hamburger-checkbox"
        />
        <Box
          id={`hamburger-menu-${data.id}`}
          className="responsive-menu block-header"
          sx={{ width: '100%' }}
          style={{
            pointerEvents: (isEditing || isTemplate) ? 'none' : 'auto',
          }}
        >
          {hamburgerMenu}
        </Box>

        <HeaderMenuSettingsModal
          hasJustifyOptions={false}
          menuItems={props.menus ?? []}
          settings={props.settings}
          open={open}
          closeHandler={(newItems?: LinkData[]) => {
            if (newItems) {
              props.menus = newItems;
              editHandler(data.id, props);
            }
            handleClose();
          }}
          updateHandler={(settings) => {
            props.settings = settings;
          }}
          blockData={blockData}
        />

      </Grid>
      {!isEditing && (
        <Box
          sx={{
            backgroundColor: `${props.url.solidColor ?? backgroundColor} !important`,
          }}
        />
      )}
    </>
  );
}

export default BlockHeader;
